import { Box, Typography, Avatar, Divider, UserSocialMediaEditor, InputBase, Link, Loader, UserFlag, 
    TransportModeSelector, ImageUpload
 } from "src/components";
import { useState } from "react";
import { updateProfile, updateUserImages, useAuth } from 'src/utils';
import CountrySelector from './country-selector';
import { useSnackbar } from 'src/hooks';

export default function EditProfile({ handleCurrentViewChange, handleSuccess, handleFailure }) {
    const [isLoading, setIsLoading] = useState(false);
    const [isPickingFile, setIsPickingFile] = useState(false);
    const [showCountryPicker, setShowCountryPicker] = useState(false);

    const { user, fetchUser } = useAuth();
    const { openSnackbar } = useSnackbar();

    const [form, setForm] = useState({
        name: user.name || "",
        bio: user.bio || "",
        countryIsoAlpha2: user.countryIsoAlpha2 || "",
        instagramHandle: user.instagramHandle || "",
        socialMediaLinks: user.socialMediaLinks?.length ? user.socialMediaLinks : [""],
        profileImage: null,
        previewProfileImage: user.profileImageUrl || null,
        vehicleImage: null,
        previewVehicleImage: user.vehicleImageUrl || null,
        transportMode: user.transportMode || "Car"
    });

    const [originalForm] = useState({
        name: user.name || "",
        bio: user.bio || "",
        countryIsoAlpha2: user.countryIsoAlpha2 || "",
        instagramHandle: user.instagramHandle || "",
        socialMediaLinks: user.socialMediaLinks?.length ? user.socialMediaLinks : [""],
        profileImage: null,
        previewProfileImage: user.profileImageUrl || null,
        vehicleImage: null,
        previewVehicleImage: user.vehicleImageUrl || null,
        transportMode: user.transportMode || "Car"
    });

    const hasChanges = JSON.stringify(form) !== JSON.stringify(originalForm);

    const handleInputChange = (field) => (e) => {
        const value = e.target.value;
        setForm((prevForm) => ({
            ...prevForm,
            [field]: value,
        }));
    };

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm({
                ...form,
                profileImage: file,
                previewProfileImage: URL.createObjectURL(file),
            });
        }
    };

    const handleVehicleImageChange = (file) => {
        if (file) {
            setForm({
                ...form,
                vehicleImage: file,
                previewVehicleImage: URL.createObjectURL(file),
            });
        }
    };

    const handleSave = async () => {
        try {
            setIsLoading(true);

            let uploadedUrls = {};
            if (form.profileImage || form.vehicleImage) {
                uploadedUrls = await updateUserImages(form.profileImage, form.vehicleImage);
            }

            // Prepare payload with only successfully uploaded images
            const payload = {
                name: form.name,
                bio: form.bio,
                countryIsoAlpha2: form.countryIsoAlpha2,
                instagramHandle: form.instagramHandle,
                socialMediaLinks: form.socialMediaLinks.filter(Boolean),
                transportMode: form.transportMode,
                profileImageUrl: uploadedUrls.profileImageUrl || form.existingProfileImage,
                vehicleImageUrl: uploadedUrls.vehicleImageUrl || form.existingVehicleImage
            };

            await updateProfile(payload);
            await fetchUser();

            // Notify user of partial failures
            const failedImages = [];
            if (uploadedUrls.profileImageError) failedImages.push("Profile");
            if (uploadedUrls.vehicleImageError) failedImages.push("Vehicle");

            if (failedImages.length) {
                handleFailure(`Image Upload Failed: ${failedImages.join(", ")}`);
            } else {
                handleSuccess?.("Profile Updated.");
            }

            handleCurrentViewChange("AccountSummary");
        } catch (error) {
            console.error("Save failed:", error);
            handleFailure('Profile update failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeAvatarClick = () => {
        setIsPickingFile(true);
        setTimeout(() => setIsPickingFile(false), 4000);
    };

    return (
    <Box sx={{pb: 8}}>

        <Box
            position="sticky"
            top={0}
            zIndex={10}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            bgcolor='#fff'
            pb={1.5}>
                <Link onClick={() => handleCurrentViewChange("AccountSummary")} sx={{
                    display: 'flex', 
                    width: '60',
                    textDecoration: 'none', 
                    cursor: 'pointer', 
                    alignItems: 'center', 
                    justifyContent: 'flex-start'}}>
                    <Typography variant="body2" fontWeight={600}>
                        Cancel
                    </Typography>
                </Link>

                <Typography variant="h6" fontWeight={600}>
                    Edit
                </Typography>

                {hasChanges ? (
                    <Link onClick={handleSave} sx={{display: 'flex', textDecoration: 'none', cursor: 'pointer', alignItems: 'center'}}>
                        <Typography variant="body2" fontWeight={600}>
                            Save
                        </Typography>
                    </Link>
                ):<Box style={{width: 22}}></Box>}
        </Box>

        <Divider sx={{mb: 2}}/>

        {showCountryPicker ? (
            <CountrySelector
                onClose={() => setShowCountryPicker(false)}
                onSelect={(code) => {
                    setForm({ ...form, countryIsoAlpha2: code });
                    setShowCountryPicker(false);
                }}
                selected={form.countryIsoAlpha2}
            />
        ) : <>

        <Typography variant="h6" mb={1}>
            Profile
        </Typography>
        <Box
            borderRadius={2}
            p={1}
            mb={2}
            sx={{border: '1px solid #D3D3D3'}}>

            <InlineEditableRow label="Name">
                <InputBase
                    value={form.name}
                    onChange={handleInputChange("name")}
                    fullWidth
                    sx={(theme) => ({
                        ...theme.typography.body2
                    })}
                />
            </InlineEditableRow>

            <Divider sx={{ my: 1 }} />

            <InlineEditableRow label="Avatar">
                <Box position="relative">
                    {/*<input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/heic,image/heif"
                        onChange={(e) => {
                            handleProfileImageChange(e);
                            setIsPickingFile(false);
                        }}
                        style={{ display: "none" }}
                        id="avatar-upload"
                    />
                    <label htmlFor="avatar-upload" onClick={handleChangeAvatarClick}>
                        <Avatar
                            src={form.previewProfileImage}
                            sx={{ width: 34, height: 34, cursor: 'pointer' }}
                        />
                    </label>
                    {isPickingFile && (
                        <Loader fullscreen={true} />
                    )}*/}
                    <ImageUpload
                        previewUrl={form.previewProfileImage}
                        onUpload={(file, preview) => setForm({ ...form, profileImage: file, previewProfileImage: preview })}
                        circular={true}
                        showClear={false}
                        size={34} 
                        aspect={1}
                        />
                </Box>
            </InlineEditableRow>

            <Divider sx={{ my: 1 }} />

            <InlineEditableRow label="Country">
                <Link onClick={() => setShowCountryPicker(true)} sx={{ textDecoration: "none", cursor: 'pointer', display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                    <Typography variant="body2" fontWeight={600}>
                        {form.countryIsoAlpha2 || "Select a country"}
                    </Typography>
                    {form.countryIsoAlpha2 && (<UserFlag countryCode={form.countryIsoAlpha2} sx={{ml: 1}} />)}
                </Link>
            </InlineEditableRow>

            <Divider sx={{ mt: 1, mb: 2 }} />

            <UserSocialMediaEditor 
                instagramHandle={form.instagramHandle}
                socialMediaLinks={form.socialMediaLinks}
                onChangeInstagram={(newIg) => setForm({ ...form, instagramHandle: newIg })}
                onChangeLinks={(newLinks) => setForm({ ...form, socialMediaLinks: newLinks })}
                />
        </Box>

        <Typography variant="h6" mb={1}>
            Bio
        </Typography>
        <Box
            borderRadius={2}
            p={2}
            mb={2}
            sx={{border: '1px solid #D3D3D3'}}>

            <InputBase
                value={form.bio}
                onChange={handleInputChange("bio")}
                placeholder="Tell us about yourself"
                multiline
                fullWidth
                minRows={3}
                inputProps={{ maxLength: 250 }}
                sx={(theme) => ({
                    ...theme.typography.body2
                })}/>
        </Box>

        <Typography variant="h6" mb={1}>
            You and Your Rig
        </Typography>
        <Box mb={2}>
            {/*<ImageUpload
                previewUrl={form.previewVehicleImage}
                onUpload={handleVehicleImageChange}
                showClear={false} />*/}
            <ImageUpload
                previewUrl={form.previewVehicleImage}
                onUpload={(file, preview) => setForm({ ...form, vehicleImage: file, previewVehicleImage: preview })}
                aspect={1}
                size={160}  
                showClear={false}
                />
        </Box>

        <TransportModeSelector
            value={form.transportMode}
            onChange={(newMode) =>
                setForm(prev => ({ ...prev, transportMode: newMode }))
            }/>

        </>}

        {isLoading && (
            <Loader />
        )}
    </Box>
    );
}

// Helper component for inline labels
function InlineEditableRow({ label, children }) {
    return (
        <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{minWidth: 60}}>
                <Typography variant="body2" fontWeight={600}>{label}</Typography>
            </Box>
            <Box flexGrow={1}>{children}</Box>
        </Box>
    );
}
